import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PROVIDER_PATHS } from "@/constants/paths";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useSnackbar } from "@/hooks/useSnackbar";
import { GROUP_NOTE_QUERY_KEY } from "@/queries/group-visit/use-group-note-by-session-id";
import { GROUP_SESSION_DETAIL_QUERY_KEY } from "@/queries/group-visit/use-group-session-by-id";
import { GROUP_SESSION_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-sessions";
import { useSignOffGroupNote } from "@/queries/group-visit/use-sign-off-group-note";
import { useGetSignatureByProviderID } from "@/queries/signature/use-get-signature-by-providerID";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import { SESSION_SIGN_OFF_NOTE } from "../group-visit-constant";
import { NavigationRunner } from "../group-visit-types";

export type UseSessionSignOff = {
  /** Signs the shared note with the logged-in provider and closes the session. */
  signOff: (providerNote?: string) => Promise<void>;
  isSubmitting: boolean;
};

/**
 * Sign-off for the session's shared group note. The finalize dialog carries no
 * signature pad, so the provider's saved signature is the one that is sent.
 *
 * @author Rajkumar Rathod
 */
export const useSessionSignOff = (
  sessionId?: string,
  runNavigation: NavigationRunner = (navigationFn) => navigationFn()
): UseSessionSignOff => {
  const client = useQueryClient();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const [me] = useCurrentUser();
  const providerId = me?.uuid ?? "";

  const { data: signature } = useGetSignatureByProviderID({
    providerUuid: providerId,
    xTENANTID: getXtenantId(),
  });
  const { mutateAsync: signOffGroupNote, isPending } = useSignOffGroupNote();

  const signOff = useCallback(
    async (providerNote?: string) => {
      if (!sessionId || !providerId) return;

      try {
        const res = await signOffGroupNote({
          sessionId,
          encounterSignOff: {
            providerId,
            provider: providerId,
            providerNote: providerNote?.trim() || SESSION_SIGN_OFF_NOTE,
            providerSignature:
              (signature?.data as { providerSignature?: string } | undefined)
                ?.providerSignature ?? undefined,
          },
        });
        showSnackbar(getSuccessSnackbar(res));

        client.invalidateQueries({
          queryKey: [GROUP_NOTE_QUERY_KEY, sessionId],
        });
        client.invalidateQueries({
          queryKey: [GROUP_SESSION_DETAIL_QUERY_KEY, sessionId],
        });
        client.invalidateQueries({ queryKey: [GROUP_SESSION_LIST_QUERY_KEY] });
        runNavigation(() => navigate(PROVIDER_PATHS.GROUP_APPOINTMENTS));
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [
      sessionId,
      providerId,
      signature,
      signOffGroupNote,
      showSnackbar,
      client,
      navigate,
      runNavigation,
    ]
  );

  return { signOff, isSubmitting: isPending };
};
